import type { UploaderContentOptions } from './components/UploaderContent';
import type { UploaderMeta, UploaderTriggerRenderProps } from './types';
import type { UploaderControllerProps } from './utils/useUploaderController';
import type { ReactNode } from 'react';

import { UppyContextProvider } from '@uppy/react';
import '@uppy/react/css/style.css';

import { UploaderDialog } from './components/UploaderDialog';
import { UploaderTrigger } from './components/UploaderTrigger';
import { useUploaderController } from './utils/useUploaderController';
import { useUploaderModal } from './utils/useUploaderModal';

type P<TMeta extends UploaderMeta = UploaderMeta> = {
  dialogTitle?: string;
  isDisabled?: boolean;
  openModalButtonIcon?: ReactNode;
  openModalButtonText?: string;
  renderTrigger?: (props: UploaderTriggerRenderProps) => ReactNode;
} & UploaderContentOptions<TMeta> &
  UploaderControllerProps<TMeta> &
  ComponentStyling;

/**
 * Uploads files to Azure Blob Storage from a modal dialog opened by a button.
 * The user drops or browses for files, uploads them directly to Azure via
 * per-file SAS URLs from the backend, and confirms the result.
 *
 * Use it where uploading is a side action of a page, e.g. an "Upload" button
 * in a list header. Use `UploaderInline` when the upload is part of the page
 * itself, and `FileSelector` when files are only read in the browser and
 * never uploaded.
 *
 * ### Upload flow
 * 1. The user opens the modal and selects files.
 * 2. The primary button starts the upload: `getUploadLink` is called for
 *    each file to get a SAS URL + file ID, then the file is PUT to Azure.
 * 3. On completion, `onSuccess` runs immediately when `confirmAfterUpload` is
 *    enabled; otherwise the primary button turns into a confirm button.
 *
 * ### Retry behavior
 * If an upload fails the primary button starts the upload again, so the
 * user can retry. Failed files are skipped in the `onSuccess` payload. If
 * `onSuccess` throws, the files stay queued and its message is shown inline.
 *
 * ### Closing
 * Closing the dialog cancels running uploads and discards the queued files.
 * It cannot be closed while files are uploading or `onSuccess` is running.
 *
 * @param getUploadLink - Async function that fetches a `{ sasLink, fileId }`
 *   upload link for a single file from the backend.
 * @param isDisabled - Whether the uploader is disabled.
 * @param onAfterUpload - Optional async callback invoked per-file after the
 *   file has been uploaded to Azure but before it is marked as successfully
 *   uploaded. Receives the uploaded file and its Uppy metadata. Use it to run
 *   server-side validation. If the callback throws, the file is treated as a
 *   failed upload and the error is shown inline.
 * @param onSuccess - Called when the user confirms the upload. Receives an
 *   array of successfully uploaded files, each containing the Azure `fileId`,
 *   `fileName`, `type`, `fileSize`, and per-file metadata. The dialog stays
 *   open while an async callback runs and shows a thrown error inline.
 * @param onError - Called for each file that fails to upload. Receives the
 *   file name and the underlying `Error` (e.g. a CORS or network error).
 * @param onFileAdded - Called when a file is added to the queue.
 * @param onStartUpload - Called when an upload batch begins, before any
 *   requests are made. Receives the upload ID and the list of files.
 * @param onUploadProgress - Called on each progress tick for a file. Receives
 *   the file and `{ bytesUploaded, bytesTotal }`.
 * @param onUploadComplete - Called once when all files in a batch have finished
 *   (successfully or not). Receives the full `UploadResult` with `successful`
 *   and `failed` arrays.
 * @param dialogTitle - Heading shown inside the modal. Falls back to the
 *   `ui:uploader.dialogTitle` translation.
 * @param openModalButtonText - Label of the button that opens the modal.
 *   Falls back to the `ui:uploader.openModalButton` translation.
 * @param openModalButtonIcon - Icon shown on the button that opens the
 *   modal. Defaults to a plus icon.
 * @param confirmButtonText - Label of the button that confirms a finished
 *   upload, given the number of successfully uploaded files. Falls back to
 *   the `ui:uploader.confirmButton` translation (which pluralizes on
 *   `count`).
 * @param uploadButtonText - Label of the button that starts the upload. Falls
 *   back to the `ui:uploader.startUploadButton` translation.
 * @param confirmAfterUpload - When `true`, a successful manual upload
 *   immediately calls `onSuccess` instead of requiring a second click.
 * @param hintText - Optional hint rendered below the dialog title.
 * @param restrictions - Uppy file restrictions (allowed types, max size, etc.).
 * @param showRemoveAllFiles - Whether to show the action that removes all
 *   selected files. Defaults to `true`.
 * @param showRestrictionsHint - Whether to show the restrictions hint below the
 *   dropzone. Defaults to `true`.
 * @param uppyLocaleOverrides - Optional per-instance overrides for Uppy's
 *   internal locale strings (restriction errors, progress messages, etc.).
 * @param autoProceed - When `true` Uppy starts uploading as soon as files are
 *   added, without waiting for the user to click the upload button.
 * @param renderFileList - Optional replacement for the built-in file list.
 *   Receives queued files and controls for removal and metadata updates.
 * @param renderTrigger - Optional replacement for the button that opens the
 *   dialog. Receives the disabled state and an `open` callback.
 * @param isFileReady - Optional readiness predicate evaluated for every queued
 *   file. Upload stays disabled until every file passes.
 */
export function Uploader<TMeta extends UploaderMeta = UploaderMeta>(
  props: P<TMeta>,
) {
  const modal = useUploaderModal(props);
  const controller = useUploaderController(props, modal.closeModal);

  return (
    <UppyContextProvider uppy={controller.uppy}>
      <UploaderTrigger
        className={props.className}
        isDisabled={Boolean(props.isDisabled)}
        onOpen={modal.handleOpen}
        openModalButtonIcon={props.openModalButtonIcon}
        openModalButtonText={modal.resolvedOpenModalButtonText}
        renderTrigger={props.renderTrigger}
        style={props.style}
      />

      <UploaderDialog
        confirmAfterUpload={props.confirmAfterUpload}
        confirmButtonText={props.confirmButtonText}
        dialogTitle={modal.resolvedDialogTitle}
        errorMessage={controller.errorMessage}
        hintText={props.hintText}
        isFileReady={props.isFileReady}
        isConfirming={controller.isConfirming}
        isOpen={modal.isOpen}
        onClose={controller.handleClose}
        onSuccess={controller.confirmUpload}
        onUploadError={controller.reportError}
        renderFileList={props.renderFileList}
        restrictions={controller.validatedRestrictions}
        showRemoveAllFiles={props.showRemoveAllFiles}
        showRestrictionsHint={props.showRestrictionsHint}
        uploadButtonText={props.uploadButtonText}
        uppy={controller.uppy}
      />
    </UppyContextProvider>
  );
}
